import React from 'react'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import type { KambiSportsSelection } from '@mobi/betslip/types'
import { isKambiSportsSelection } from '@mobi/betslip/helpers/typeGuards'
import { observeImmutable } from '@core/Components/HOCs'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { useGetKambiRewardsForSelection } from './hooks/useGetKambiRewardsForSelection'
import { SelectedReward } from './Components/SelectedReward'
import { RewardSelection } from './Components/RewardSelection'

export const KambiRewardContainer: React.ComponentClass = observeImmutable(
  quickbetState$,
  ({ record }) => {
    const quickbetState = record.toJS() as QuickbetState
    if (!isKambiSportsSelection(quickbetState.selection)) return null

    return (
      <KambiRewardContainerComponent
        canConfirmBet={quickbetState.canConfirmBet}
        selection={quickbetState.selection}
        selectedReward={quickbetState.selectedKambiReward}
      />
    )
  }
)

// ===============
// Local Component
// ===============

const KambiRewardContainerComponent: React.FC<{
  canConfirmBet: boolean
  selection: KambiSportsSelection
  selectedReward: KambiValidReward | null
}> = ({ canConfirmBet, selection, selectedReward }) => {
  const hasCheckedAutoSelectByDefaultRef = React.useRef(false)

  const setHasCheckedAutoSelectByDefault = React.useCallback(() => {
    hasCheckedAutoSelectByDefaultRef.current = true
  }, [])

  const rewardInfo = useGetKambiRewardsForSelection(selection)

  if (!rewardInfo || (!selectedReward && canConfirmBet)) return null

  if (canConfirmBet) {
    if (!selectedReward) return null

    return <SelectedReward selectedReward={selectedReward} />
  }

  const allValidRewards = rewardInfo
    ? [
        ...(rewardInfo.validRewards ?? []),
        ...(rewardInfo.validGroupRewards ?? []),
        //should not be displayed to the punter
        // ...(rewardInfo.implicitGroupRewards ?? []),
      ].map(validReward => validReward.reward)
    : []

  if (allValidRewards.length === 0) return null

  return (
    <RewardSelection
      rewards={allValidRewards}
      selectedReward={selectedReward}
      setHasCheckedAutoSelectByDefault={setHasCheckedAutoSelectByDefault}
      hasCheckedAutoSelectByDefaultRef={hasCheckedAutoSelectByDefaultRef}
    />
  )
}
